/**
 * Tiny safe arithmetic for object definitions (no `eval`): numbers, the
 * constant `pi`, the live size (`width`, `depth`, `height`), param keys
 * (booleans read as 1/0, choices as the option's index), formulas,
 * `+ - * /`, comparisons `< <= > >= == !=` (1 or 0), parentheses, unary
 * minus and a few functions — `min max abs sign round floor ceil sqrt pow
 * hypot mod clamp(x, lo, hi) if(condition, then, else) sin cos tan atan2`.
 *
 * Used wherever a spec value may follow the live object (see spec.ts), e.g.
 * `width / 2 - flightWidth`.
 */

export type ExpressionNode =
  | { t: 'num'; v: number }
  | { t: 'var'; name: string }
  | { t: 'neg'; a: ExpressionNode }
  | { t: 'bin'; op: BinaryOperator; a: ExpressionNode; b: ExpressionNode }
  | { t: 'call'; fn: ExpressionFunction; args: ExpressionNode[] };

type BinaryOperator = '+' | '-' | '*' | '/' | '<' | '<=' | '>' | '>=' | '==' | '!=';
const COMPARISONS: readonly BinaryOperator[] = ['<=', '>=', '==', '!=', '<', '>'];

const FUNCTIONS = {
  min: (args: number[]) => Math.min(...args),
  max: (args: number[]) => Math.max(...args),
  abs: (args: number[]) => Math.abs(args[0]),
  round: (args: number[]) => Math.round(args[0]),
  floor: (args: number[]) => Math.floor(args[0]),
  ceil: (args: number[]) => Math.ceil(args[0]),
  sqrt: (args: number[]) => Math.sqrt(Math.max(0, args[0])),
  clamp: (args: number[]) => Math.min(Math.max(args[0], args[1]), args[2]),
  sign: (args: number[]) => Math.sign(args[0]),
  pow: (args: number[]) => Math.pow(args[0], args[1]),
  hypot: (args: number[]) => Math.hypot(...args),
  // Always ≥ 0 for a positive divisor (alternating cells: mod(i, 2)).
  mod: (args: number[]) => args[0] - args[1] * Math.floor(args[0] / args[1]),
  if: (args: number[]) => (args[0] !== 0 ? args[1] : args[2]),
  sin: (args: number[]) => Math.sin(args[0]),
  cos: (args: number[]) => Math.cos(args[0]),
  tan: (args: number[]) => Math.tan(args[0]),
  atan2: (args: number[]) => Math.atan2(args[0], args[1]),
} as const;

export type ExpressionFunction = keyof typeof FUNCTIONS;

const ARITY: Record<ExpressionFunction, [number, number]> = {
  min: [1, 16],
  max: [1, 16],
  abs: [1, 1],
  round: [1, 1],
  floor: [1, 1],
  ceil: [1, 1],
  sqrt: [1, 1],
  clamp: [3, 3],
  sign: [1, 1],
  pow: [2, 2],
  hypot: [1, 16],
  mod: [2, 2],
  if: [3, 3],
  sin: [1, 1],
  cos: [1, 1],
  tan: [1, 1],
  atan2: [2, 2],
};

/** Function names (for editor hints). */
export const EXPRESSION_FUNCTIONS = Object.keys(ARITY) as ExpressionFunction[];

/** Names with a fixed meaning; params and formulas can't use them as keys. */
export const EXPRESSION_RESERVED_NAMES: readonly string[] = ['pi', 'i', 'count', 'pulled', ...Object.keys(ARITY)];

/** The live-size variables every expression can use. */
export const EXPRESSION_SIZE_VARIABLES = ['width', 'depth', 'height'] as const;

export const MAX_EXPRESSION_LENGTH = 400;
const MAX_DEPTH = 32;

export type ExpressionParseResult = { ok: true; node: ExpressionNode } | { ok: false; error: string };

type Token = { k: 'num'; v: number } | { k: 'id'; v: string } | { k: 'op'; v: string };

function tokenize(src: string): Token[] | string {
  const tokens: Token[] = [];
  let i = 0;
  while (i < src.length) {
    const c = src[i];
    if (/\s/.test(c)) {
      i++;
    } else if (/[0-9.]/.test(c)) {
      const m = /^(\d+\.?\d*|\.\d+)(e[+-]?\d+)?/i.exec(src.slice(i));
      if (!m) return `bad number at ${i + 1}`;
      tokens.push({ k: 'num', v: Number(m[0]) });
      i += m[0].length;
    } else if (/[a-zA-Z_]/.test(c)) {
      const m = /^[a-zA-Z_][a-zA-Z0-9_]*/.exec(src.slice(i));
      if (!m) return `bad name at ${i + 1}`;
      tokens.push({ k: 'id', v: m[0] });
      i += m[0].length;
    } else if ((c === '<' || c === '>' || c === '=' || c === '!') && src[i + 1] === '=') {
      tokens.push({ k: 'op', v: `${c}=` });
      i += 2;
    } else if ('+-*/(),<>'.includes(c)) {
      tokens.push({ k: 'op', v: c });
      i++;
    } else {
      return `unexpected "${c}"`;
    }
  }
  return tokens;
}

export function parseExpression(src: string): ExpressionParseResult {
  if (src.length > MAX_EXPRESSION_LENGTH) return { ok: false, error: `longer than ${MAX_EXPRESSION_LENGTH} characters` };
  const tokens = tokenize(src);
  if (typeof tokens === 'string') return { ok: false, error: tokens };
  if (tokens.length === 0) return { ok: false, error: 'empty' };
  let pos = 0;
  const peek = (): Token | undefined => tokens[pos];
  const isOp = (v: string): boolean => {
    const t = peek();
    return t?.k === 'op' && t.v === v;
  };

  // Recursive descent: expr := sum (cmp sum)?; sum := term (('+'|'-') term)*; term := unary (('*'|'/') unary)*;
  // unary := '-' unary | primary.
  const expr = (depth: number): ExpressionNode => {
    const node = sum(depth);
    const t = peek();
    const op = t?.k === 'op' ? COMPARISONS.find((c) => c === t.v) : undefined;
    if (!op) return node;
    pos++;
    return { t: 'bin', op, a: node, b: sum(depth) };
  };
  const sum = (depth: number): ExpressionNode => {
    let node = term(depth);
    while (isOp('+') || isOp('-')) {
      const op = isOp('+') ? '+' : '-';
      pos++;
      node = { t: 'bin', op, a: node, b: term(depth) };
    }
    return node;
  };
  const term = (depth: number): ExpressionNode => {
    let node = unary(depth);
    while (isOp('*') || isOp('/')) {
      const op = isOp('*') ? '*' : '/';
      pos++;
      node = { t: 'bin', op, a: node, b: unary(depth) };
    }
    return node;
  };
  const unary = (depth: number): ExpressionNode => {
    if (depth > MAX_DEPTH) throw new Error('nested too deeply');
    if (isOp('-')) {
      pos++;
      return { t: 'neg', a: unary(depth + 1) };
    }
    if (isOp('+')) {
      pos++;
      return unary(depth + 1);
    }
    return primary(depth + 1);
  };
  const primary = (depth: number): ExpressionNode => {
    const t = peek();
    if (!t) throw new Error('unexpected end');
    if (t.k === 'num') {
      pos++;
      return { t: 'num', v: t.v };
    }
    if (t.k === 'op' && t.v === '(') {
      pos++;
      const inner = expr(depth + 1);
      if (!isOp(')')) throw new Error('missing ")"');
      pos++;
      return inner;
    }
    if (t.k === 'id') {
      pos++;
      if (!isOp('(')) return t.v === 'pi' ? { t: 'num', v: Math.PI } : { t: 'var', name: t.v };
      const fn = Object.keys(FUNCTIONS).find((f): f is ExpressionFunction => f === t.v);
      if (!fn) throw new Error(`unknown function "${t.v}"`);
      pos++;
      const args: ExpressionNode[] = [];
      if (!isOp(')')) {
        args.push(expr(depth + 1));
        while (isOp(',')) {
          pos++;
          args.push(expr(depth + 1));
        }
      }
      if (!isOp(')')) throw new Error('missing ")"');
      pos++;
      const [lo, hi] = ARITY[fn];
      if (args.length < lo || args.length > hi) throw new Error(`${fn} takes ${lo === hi ? lo : `${lo}–${hi}`} arguments`);
      return { t: 'call', fn, args };
    }
    throw new Error(`unexpected "${t.v}"`);
  };

  try {
    const node = expr(0);
    if (pos < tokens.length) return { ok: false, error: `unexpected "${String(tokens[pos].v)}"` };
    return { ok: true, node };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : String(e) };
  }
}

/** Every variable an expression reads. */
export function expressionVariables(node: ExpressionNode, out: Set<string> = new Set()): Set<string> {
  switch (node.t) {
    case 'var':
      out.add(node.name);
      break;
    case 'neg':
      expressionVariables(node.a, out);
      break;
    case 'bin':
      expressionVariables(node.a, out);
      expressionVariables(node.b, out);
      break;
    case 'call':
      node.args.forEach((a) => expressionVariables(a, out));
      break;
    case 'num':
      break;
  }
  return out;
}

function applyBinary(op: BinaryOperator, a: number, b: number): number {
  switch (op) {
    case '+':
      return a + b;
    case '-':
      return a - b;
    case '*':
      return a * b;
    case '/':
      return a / b;
    case '<':
      return a < b ? 1 : 0;
    case '<=':
      return a <= b ? 1 : 0;
    case '>':
      return a > b ? 1 : 0;
    case '>=':
      return a >= b ? 1 : 0;
    case '==':
      return Math.abs(a - b) < 1e-9 ? 1 : 0;
    case '!=':
      return Math.abs(a - b) < 1e-9 ? 0 : 1;
  }
}

/** Evaluate; unknown variables read 0, division by zero and other non-finite results give 0. */
export function evaluateExpression(node: ExpressionNode, vars: Readonly<Record<string, number>>): number {
  const ev = (n: ExpressionNode): number => {
    switch (n.t) {
      case 'num':
        return n.v;
      case 'var':
        return vars[n.name] ?? 0;
      case 'neg':
        return -ev(n.a);
      case 'bin':
        return applyBinary(n.op, ev(n.a), ev(n.b));
      case 'call':
        return FUNCTIONS[n.fn](n.args.map(ev));
    }
  };
  const result = ev(node);
  return Number.isFinite(result) ? result : 0;
}

/**
 * Parse and check an expression against the names it may use; null when
 * fine, else the problem.
 */
export function checkExpression(src: string, allowed: ReadonlySet<string>): string | null {
  const parsed = parseExpression(src);
  // `in` narrowing: libs compiling this file without strictNullChecks can't narrow on `ok`.
  if ('error' in parsed) return parsed.error;
  for (const name of expressionVariables(parsed.node)) if (!allowed.has(name)) return `unknown name "${name}"`;
  return null;
}
